export type LangMap<T = string> = Record<string, T>;

/**
 * 言語コードを正規化する。
 * 中国語のバリアントはstroly-viewerのadjustLangCodeと同じルールで
 * zh-Hans/zh-Hantに変換し、それ以外はベース言語に正規化する。
 *
 * - zh, zh-CN → zh-Hans（簡体字）
 * - zh-TW, zh-HK → zh-Hant（繁体字）
 * - zh-Hans, zh-Hant → そのまま維持
 * - en-US → en、ja-JP → ja など
 */
export function adjustLangCode(lang: string): string {
  const lower = lang.toLowerCase();

  if (lower === 'zh' || lower === 'zh-cn') {
    return 'zh-Hans';
  }
  if (lower === 'zh-tw' || lower === 'zh-hk') {
    return 'zh-Hant';
  }
  if (lower === 'zh-hans' || lower === 'zh-hant') {
    // 既に正規化済み
    return lang;
  }

  return lower.split('-')[0];
}

/**
 * ユーザーの優先言語リストを返す。
 * ブラウザの言語設定リストをadjustLangCodeで正規化し、重複を除去して返す。
 * コンテンツ言語の解決に使用する（UI言語とは独立）。
 */
export function getPreferredLangs(): string[] {
  const raw: string[] = [];

  const browserLangs = window.navigator.languages
    ? Array.from(window.navigator.languages)
    : [];
  if (browserLangs.length > 0) {
    raw.push(...browserLangs);
  } else if (window.navigator.language) {
    raw.push(window.navigator.language);
  }

  // 言語コードを正規化し、重複を除去（順序は維持）
  const seen = new Set<string>();
  const result: string[] = [];
  for (const lang of raw) {
    const adjusted = adjustLangCode(lang);
    if (!seen.has(adjusted)) {
      seen.add(adjusted);
      result.push(adjusted);
    }
  }

  return result;
}

/**
 * LangMapからユーザーの優先言語リストに基づいて最適な値を取得する。
 * 優先順位: ユーザーの言語リスト順 → その他の利用可能な言語
 */
export function resolveLangMapValue(
  langMap: LangMap | undefined,
  langs: string[] | string,
): string {
  if (!langMap) {
    return '';
  }

  const langList = typeof langs === 'string' ? [langs] : langs;

  // ユーザーの優先言語リスト順にチェック
  for (const lang of langList) {
    if (langMap[lang] && langMap[lang].length > 0) {
      return langMap[lang];
    }
  }

  // どれもなければ、利用可能な最初の値を返す
  for (const key of Object.keys(langMap)) {
    if (langMap[key] && langMap[key].length > 0) {
      return langMap[key];
    }
  }

  return '';
}
